import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef} from 'react';
import PropTypes from "prop-types";
import {useHistory} from "react-router-dom";
import {actions, initialState} from './actions';
import {reducer} from './LoginInfo.reducer';
import {useAuth} from '../../../context/authContext';

const LoginInfoContext = createContext();
const MFA_CHALLENGE_STORAGE_KEY = "ce-mfa-challenge";

const LoginInfoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState, (base) => {
    const fromStorage = sessionStorage.getItem(MFA_CHALLENGE_STORAGE_KEY);
    if (!fromStorage) {
      return base;
    }
    try {
      return {
        ...base,
        mfaChallenge: JSON.parse(fromStorage)
      };
    } catch (error) {
      console.warn("Invalid MFA challenge data in session storage. Clearing stored value.", error);
      sessionStorage.removeItem(MFA_CHALLENGE_STORAGE_KEY);
      return base;
    }
  });
  const isMountedRef = useRef(true);
  const auth = useAuth();
  const history = useHistory();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const safeDispatch = useCallback((action) => {
    if (isMountedRef.current) {
      dispatch(action);
    }
  }, [dispatch]);

  const setMfaChallenge = useCallback((challenge) => {
    safeDispatch({ type: actions.SET_MFA_CHALLENGE, payload: challenge });
    if (challenge) {
      sessionStorage.setItem(MFA_CHALLENGE_STORAGE_KEY, JSON.stringify(challenge));
    } else {
      sessionStorage.removeItem(MFA_CHALLENGE_STORAGE_KEY);
    }
  }, [safeDispatch]);

  const showMfaChallenge = useCallback((challenge) => {
    const sources = Array.isArray(challenge.sources) ? challenge.sources : [];
    setMfaChallenge({
      ...challenge,
      username: state.username,
      selectedSource: challenge.selectedSource || sources[0] || "EMAIL"
    });
    safeDispatch({ type: actions.SET_TERMS_REQUIRED, payload: false });
    safeDispatch({ type: actions.SET_ERROR, payload: null });
    history.push("/verify");
  }, [history, safeDispatch, setMfaChallenge, state.username]);

  const onLogin = useCallback(async () => {
    setMfaChallenge(null);
    const success = await auth.login(state.username, state.password);
    safeDispatch({ type: actions.SET_LOADING, payload: false });
    if (success?.requiresMfa) {
      showMfaChallenge(success);
      return success;
    }

    if (success !== true) {
      const errorDescription = success?.response?.data?.error_description;

      if (errorDescription === 'TERMS_AND_AGREEMENTS_ACCEPTANCE_REQUIRED') {
        safeDispatch({type: actions.SET_TERMS_REQUIRED, payload: true});
        safeDispatch({type: actions.SET_ERROR, payload: null});
      } else if (errorDescription === 'PASSWORD_EXPIRED') {
        safeDispatch({type: actions.SET_ERROR, payload: 'Your password has expired. Please reset your password now to continue accessing your account by clicking on Forgot Password option.'});
      } else {
        safeDispatch({ type: actions.SET_ERROR, payload: success });
      }
    }
    return success;
  }, [auth, safeDispatch, setMfaChallenge, showMfaChallenge, state.password, state.username]);

  const onRedirect = useCallback(async () => {
    const success = await auth.loginForRedirect(state.username, state.password);
    safeDispatch({ type: actions.SET_LOADING, payload: false });
    return success;
  }, [auth, safeDispatch, state.password, state.username]);


  const onLoginAccepted = useCallback(async () => {
    safeDispatch({ type: actions.SET_LOADING, payload: true });
    const success = await auth.login(state.username, state.password, true);
    safeDispatch({ type: actions.SET_LOADING, payload: false });

    if (success?.requiresMfa) {
      showMfaChallenge(success);
    } else if (success === true) {
      safeDispatch({ type: actions.SET_TERMS_REQUIRED, payload: false });
      safeDispatch({ type: actions.SET_ERROR, payload: null });
    } else {
      safeDispatch({ type: actions.SET_ERROR, payload: success });
    }
    return success;
  }, [auth, safeDispatch, showMfaChallenge, state.password, state.username]);

  const onVerifyOtp = useCallback(async (otp) => {
    const payload = {
      otp,
      mfaToken: state.mfaChallenge?.mfaToken || state.mfaChallenge?.mfa_token,
    };

    const verificationResult = await auth.verifyOtp(payload);
    if (verificationResult === true) {
      setMfaChallenge(null);
      safeDispatch({ type: actions.SET_ERROR, payload: null });
    }
    return verificationResult;
  }, [auth, safeDispatch, setMfaChallenge, state.mfaChallenge]);

  const onResendOtp = useCallback(async () => {
    const payload = {
      mfaToken: state.mfaChallenge?.mfaToken || state.mfaChallenge?.mfa_token,
      source: state.mfaChallenge?.selectedSource || state.mfaChallenge?.sources?.[0] || "EMAIL"
    };
    const response = await auth.resendOtp(payload);
    const [error, result] = response;
    if (!error && result?.data) {
      setMfaChallenge({
        ...state.mfaChallenge,
        ...result.data,
        requiresMfa: true,
        username: state.mfaChallenge?.username || state.username
      });
    }
    return response;
  }, [auth, setMfaChallenge, state.mfaChallenge, state.username]);

  const value = useMemo(() => ({
    username: state.username,
    password: state.password,
    termsRequired: state.termsRequired,
    latestLoginFailed: state.latestLoginFailed,
    error: state.error,
    loading: state.loading,
    mfaChallenge: state.mfaChallenge,
    setUsername: (username) => {
      safeDispatch({ type: actions.SET_USERNAME, payload: username });
    },
    setLoading: (loading) => {
      safeDispatch({ type: actions.SET_LOADING, payload: loading });
    },
    setPassword: (password) => {
      safeDispatch({ type: actions.SET_PASSWORD, payload: password });
    },
    setTermsRequired: (isRequired) => {
      safeDispatch({ type: actions.SET_TERMS_REQUIRED, payload: isRequired });
    },
    setLatestLoginFailed: (failed) => {
      safeDispatch({ type: actions.SET_LOGIN_FAILED, payload: failed });
    },
    setError: (error) => {
      safeDispatch({ type: actions.SET_ERROR, payload: error });
    },
    setMfaChallenge: (challenge) => {
      setMfaChallenge(challenge);
    },
    onLogin: () => onLogin(),
    onRedirect: () =>onRedirect(),
    onLoginAccepted: () => onLoginAccepted(),
    onVerifyOtp: (otp) => onVerifyOtp(otp),
    onResendOtp: () => onResendOtp()
  }), [
    onLogin,
    onLoginAccepted,
    onRedirect,
    onResendOtp,
    onVerifyOtp,
    safeDispatch,
    setMfaChallenge,
    state.error,
    state.latestLoginFailed,
    state.loading,
    state.mfaChallenge,
    state.password,
    state.termsRequired,
    state.username
  ]);

  return (
    <LoginInfoContext.Provider value={value}>
      {children}
    </LoginInfoContext.Provider>
  )
};

LoginInfoProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useLoginInfoContext = () => useContext(LoginInfoContext);

export {
  LoginInfoProvider,
  useLoginInfoContext,
}
